import { Component } from '@theme/component';
import {
  debounce,
  mediaQueryDesktop,
  onDocumentLoaded,
  onDocumentReady,
  requestIdleCallback,
  setHeaderMenuStyle,
} from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/** Skim filter: pointer must dwell this long before MegaMenuHoverEvent fires. */
const HOVER_COMMIT_DELAY_MS = 150;

/** Grace period before closing so the pointer can cross padding/border into the mega menu. */
const DEACTIVATE_DELAY_MS = 120;

/** Devices that don't keep hover (touch / coarse) — close via outside tap / toggle, not pointerleave. */
const mediaHoverNone = matchMedia('(hover: none)');
const mediaPointerCoarse = matchMedia('(pointer: coarse)');

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /**
   * @type {ResizeObserver | null}
   */
  #submenuResizeObserver = null;

  /**
   * Re-places feature tiles when section hydration restores empty slots,
   * or when the feature store mounts after this element connects.
   * @type {MutationObserver | null}
   */
  #featuresDomObserver = null;

  /** @type {number | undefined} */
  #remeasureRaf;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #hoverDispatchTimer;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #deactivateTimer;

  /** @type {((this: HTMLImageElement, ev: Event) => void) | null} */
  #onSubmenuImageLoad = null;

  /**
   * Sticky open from touch/pen — pointerleave/blur must not close (they fire as soon as the finger lifts).
   * Closed via outside pointerdown or a later second tap on the same item.
   * @type {boolean}
   */
  #touchOpen = false;

  /**
   * Ignore same-item toggle until this time (ms, performance.now) so the opening
   * gesture’s re-enter / synthetic pointerenter / click cannot immediately close.
   * @type {number}
   */
  #ignoreToggleUntil = 0;

  /**
   * Suppress pointerleave closes while we inject feature DOM (replaceWith can
   * briefly leave the hit target and shut the panel mid-open).
   * @type {boolean}
   */
  #suppressLeaveClose = false;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    // Features store is a sibling later in the header — place as soon as the DOM
    // is parsed (not window.load). Waiting for load left a long window where
    // activate measured empty panels (white underlay / missing feature tiles).
    onDocumentReady(() => {
      this.#observeFeatureDom();
      this.#ensureMegaMenuContent();
    });
    // Retry on idle in case the feature store or images settle after first paint
    requestIdleCallback(() => this.#ensureMegaMenuContent());
    // Immediate attempt (works when this connects after the feature store)
    this.#observeFeatureDom();
    this.#ensureMegaMenuContent();

    // Immediate on desktop (992px) breakpoint — compact header uses drawer below this
    mediaQueryDesktop.addEventListener('change', this.#breakpointListener);
    // Overflow remeasure only: light debounce so continuous resize doesn’t thrash
    window.addEventListener('resize', this.#resizeListener);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
    document.addEventListener('pointerdown', this.#onDocumentPointerDown, true);
    setHeaderMenuStyle();
  }

  /**
   * Watch for empty feature slots reappearing (section hydration).
   * The feature store is observed once it exists in the DOM.
   */
  #observeFeatureDom() {
    this.#featuresDomObserver?.disconnect();
    this.#featuresDomObserver = new MutationObserver(() => {
      if (this.querySelector('.mega-menu__feature-slot[data-menu-key]')) {
        this.#ensureMegaMenuContent();
      }
    });
    this.#featuresDomObserver.observe(this, { childList: true, subtree: true });

    const store = document.getElementById('header-mega-menu-features');
    if (store) {
      this.#featuresDomObserver.observe(store, { childList: true, subtree: true });
    }
  }

  /**
   * Ensure feature promos are in matching slots, then remeasure if a panel is open.
   * Safe to call repeatedly — only acts on remaining empty slots.
   */
  #ensureMegaMenuContent = () => {
    const placed = this.#placeMegaMenuFeatures();

    // Feature store sits after this element in the header. Mark ready once it
    // exists so CSS can allow panels open with correct feature placement.
    if (document.getElementById('header-mega-menu-features')) {
      if (!this.hasAttribute('data-mega-ready')) {
        this.dataset.megaReady = '';
        // Attach store observer now that the node exists
        this.#observeFeatureDom();
      }
    }

    // Remeasure whenever a panel is open — hydration/slot replacement can
    // shrink --submenu-height to the content-visibility placeholder.
    if (placed || this.#state.activeItem) {
      this.#remeasureActiveSubmenu();
    }
  };

  /**
   * Place mega menu feature promos into matching parent menus.
   * Idempotent: only empty `.mega-menu__feature-slot` nodes are replaced.
   * @returns {boolean} True if any slot was filled.
   */
  #placeMegaMenuFeatures = () => {
    const sources = document.querySelectorAll('#header-mega-menu-features [data-mega-menu-feature-for]');
    const slots = this.querySelectorAll('.mega-menu__feature-slot[data-menu-key]');
    if (!sources.length || !slots.length) return false;

    let placed = false;
    this.#suppressLeaveClose = true;

    try {
      sources.forEach((source) => {
        const key = source.getAttribute('data-mega-menu-feature-for');
        if (!key) return;

        this.querySelectorAll(`.mega-menu__feature-slot[data-menu-key="${CSS.escape(key)}"]`).forEach((slot) => {
          const layout = slot.closest('.mega-menu__layout');
          const clone = /** @type {HTMLElement} */ (source.cloneNode(true));
          clone.removeAttribute('hidden');
          clone.hidden = false;
          clone.classList.remove('mega-menu__feature-source');

          slot.replaceWith(clone);
          layout?.classList.add('mega-menu__layout--has-aside');
          placed = true;
        });
      });
    } finally {
      // Next frame so any leave from replaceWith is ignored, then clear
      requestAnimationFrame(() => {
        this.#suppressLeaveClose = false;
        // Cancel a leave that may already have been scheduled
        if (this.#state.activeItem) {
          clearTimeout(this.#deactivateTimer);
          this.#deactivateTimer = undefined;
        }
      });
    }

    return placed;
  };

  /**
   * Re-read open panel height after features/images change layout.
   */
  #remeasureActiveSubmenu() {
    if (!this.#state.activeItem) return;

    // Hydration can replace the hovered node; resolve the live menuitem.
    let item = this.#state.activeItem;
    if (!item.isConnected) {
      const liveItem = this.querySelector('.menu-list__link[aria-expanded="true"]');
      if (!(liveItem instanceof HTMLElement)) return;
      item = liveItem;
      this.#state.activeItem = item;
    }

    const isOverflow = this.dataset.overflowExpanded === 'true';
    let submenu = findSubmenu(item);
    if (!submenu && isOverflow) {
      submenu = this.overflowMenu;
    }
    if (!submenu) return;

    // Force real layout — without data-active, content-visibility uses the
    // 400px intrinsic placeholder and the underlay/clip covers the links.
    submenu.dataset.active = '';
    void submenu.offsetHeight;

    this.#applySubmenuHeight(submenu, isOverflow);
    this.#scheduleSubmenuRemeasure(submenu, isOverflow);
    this.#watchSubmenuSize(submenu, isOverflow);
  }

  /**
   * Whether the event originated inside this menu (light DOM or open shadow).
   * @param {Event} event
   * @returns {boolean}
   */
  #eventIsInsideMenu(event) {
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
    if (path.includes(this)) return true;
    if (event.target instanceof Node && this.contains(event.target)) return true;
    return false;
  }

  /**
   * Close mega menu when tapping/clicking outside (needed for touch — no hover leave).
   * @param {PointerEvent} event
   */
  #onDocumentPointerDown = (event) => {
    if (!this.#state.activeItem) return;
    if (this.#eventIsInsideMenu(event)) return;
    this.#forceDeactivate();
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    mediaQueryDesktop.removeEventListener('change', this.#breakpointListener);
    window.removeEventListener('resize', this.#resizeListener);
    this.#resizeListener.cancel?.();
    document.body.removeEventListener('pointermove', this.#onPointerMove);
    document.removeEventListener('pointerdown', this.#onDocumentPointerDown, true);
    this.#featuresDomObserver?.disconnect();
    this.#featuresDomObserver = null;
    if (this.#state.activeItem) {
      this.#stopPointerTracking(this.#state.activeItem);
    }
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.#cleanupSubmenuWatchers();
    clearTimeout(this.#hoverDispatchTimer);
    this.#hoverDispatchTimer = undefined;
    clearTimeout(this.#deactivateTimer);
    this.#deactivateTimer = undefined;
  }

  /**
   * Fires as soon as the viewport crosses the desktop (992px) breakpoint.
   */
  #breakpointListener = () => {
    setHeaderMenuStyle();
    // Compact header uses drawer only — force mega menu closed
    if (!mediaQueryDesktop.matches) {
      this.#forceDeactivate();
    }
  };

  /**
   * Debounced resize: only needed for overflow-list minimum width rechecks mid-desktop.
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
    if (!mediaQueryDesktop.matches) {
      this.#forceDeactivate();
    }
  }, 50);

  #overflowSubmenuListener = () => {
    // Hover-driven overflow panel only; touch closes via document pointerdown
    if (this.#touchOpen || mediaHoverNone.matches || mediaPointerCoarse.matches) return;
    this.#deactivate();
  };

  /**
   * True for touch/pen gestures and devices that don't sustain hover.
   * @param {Event} event
   * @returns {boolean}
   */
  #isCoarsePointerEvent(event) {
    if (event instanceof PointerEvent) {
      if (event.pointerType === 'touch' || event.pointerType === 'pen') return true;
    }
    if (mediaHoverNone.matches || mediaPointerCoarse.matches) return true;
    return false;
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #pointerIdleTimer;

  /**
   * Last known pointer position for Safari hit-test reconciliation.
   * @type {{ x: number, y: number }}
   */
  #lastPointer = { x: 0, y: 0 };

  /**
   * Update the safety box idle state on the active menu item.
   * @param {PointerEvent} event
   */
  #onPointerMove = (event) => {
    const activeLink = this.#state.activeItem;
    if (!activeLink || this.#touchOpen) return;

    this.#lastPointer.x = event.clientX;
    this.#lastPointer.y = event.clientY;

    const moving = Math.abs(event.movementX) >= 1 || event.movementY >= 1;
    activeLink.dataset.safetyBox = `${moving}`;

    clearTimeout(this.#pointerIdleTimer);
    if (moving) {
      this.#pointerIdleTimer = setTimeout(() => {
        if (this.#state.activeItem) {
          this.#state.activeItem.dataset.safetyBox = 'false';
          this.#reconcilePointerTarget();
        }
      }, 50);
    } else {
      this.#reconcilePointerTarget();
    }
  };

  /**
   * Check if the pointer is over a different menu item and trigger activation if so.
   * Works around Safari not re-evaluating hit targets after pseudo-element changes.
   */
  #reconcilePointerTarget() {
    const { x, y } = this.#lastPointer;
    requestAnimationFrame(() => {
      const target = document.elementFromPoint(x, y);
      if (!target) return;
      const listItem = target.closest('.menu-list__list-item');
      if (listItem && !listItem.contains(this.#state.activeItem)) {
        listItem.dispatchEvent(new PointerEvent('pointerenter', { bubbles: false }));
      }
    });
  }

  /**
   * Begin pointer tracking for the safety box on the newly active item.
   * @param {HTMLElement} item
   * @param {HTMLElement | null} previousItem
   */
  #startPointerTracking(item, previousItem) {
    if (previousItem) {
      this.#stopPointerTracking(previousItem);
    } else {
      document.body.addEventListener('pointermove', this.#onPointerMove);
    }

    const rect = item.getBoundingClientRect();
    const isOverlap = this.headerComponent?.hasAttribute('data-submenu-overlap-bottom-row');
    const boundary = isOverlap ? this.headerComponent?.querySelector('.header__row--top') : this.headerComponent;
    item.style.setProperty('--box-height', `${(boundary?.getBoundingClientRect().bottom ?? 0) - rect.top}px`);
  }

  /**
   * Stop pointer tracking and remove all safety box properties from an item.
   * @param {HTMLElement} item
   */
  #stopPointerTracking(item) {
    window.clearTimeout(this.#pointerIdleTimer);
    this.#pointerIdleTimer = undefined;
    item.style.removeProperty('--box-height');
    delete item.dataset.safetyBox;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    if (!(event.target instanceof Element) || !this.headerComponent) return;

    // Compact (<992px) uses the drawer — never open hover mega menus
    if (!mediaQueryDesktop.matches) return;

    // Fill any remaining feature slots before height measure (early hover / post-hydration).
    // Bail until the feature store exists so we don't paint an empty underlay.
    this.#ensureMegaMenuContent();
    if (!this.hasAttribute('data-mega-ready')) return;

    let item = findMenuItem(event.target);

    if (!item) return;

    const isCoarse = this.#isCoarsePointerEvent(event) || this.#touchOpen;

    if (item == this.#state.activeItem) {
      // Touch: a later second tap on the open item closes it. Never close on the
      // opening gesture — pointerleave→re-enter, focus, synthetic reconcile enter,
      // and the click that follows pointerenter would all fire immediately.
      if (
        isCoarse &&
        (event.type === 'pointerdown' || event.type === 'click') &&
        performance.now() >= this.#ignoreToggleUntil
      ) {
        this.#forceDeactivate();
        return;
      }
      // Same-gesture click after touch-open: keep panel open, don't follow parent URL
      if (
        this.#touchOpen &&
        event.type === 'click' &&
        findSubmenu(item) &&
        performance.now() < this.#ignoreToggleUntil
      ) {
        event.preventDefault();
      }
      // Mouse re-enter (or same-gesture re-entry): cancel a pending close
      clearTimeout(this.#deactivateTimer);
      this.#deactivateTimer = undefined;
      return;
    }

    clearTimeout(this.#deactivateTimer);
    this.#deactivateTimer = undefined;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
      const previousSubmenu = findSubmenu(previouslyActiveItem);
      if (previousSubmenu) delete previousSubmenu.dataset.active;
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    // Sticky touch mode: leave/blur after finger-up must not close the panel
    if (isCoarse || (event instanceof PointerEvent && (event.pointerType === 'touch' || event.pointerType === 'pen'))) {
      this.#touchOpen = true;
      this.#ignoreToggleUntil = performance.now() + 400;
    } else {
      this.#touchOpen = false;
      this.#ignoreToggleUntil = 0;
    }

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    const isOverflow = !isDefaultSlot;

    if (submenu) {
      clearTimeout(this.#hoverDispatchTimer);
      this.#hoverDispatchTimer = undefined;
      const committedItem = item;
      if (event instanceof FocusEvent) {
        this.dispatchEvent(new MegaMenuHoverEvent());
      } else {
        this.#hoverDispatchTimer = setTimeout(() => {
          this.#hoverDispatchTimer = undefined;
          if (this.#state.activeItem === committedItem) {
            this.dispatchEvent(new MegaMenuHoverEvent());
          }
        }, HOVER_COMMIT_DELAY_MS);
      }

      // Mark submenu as active so content-visibility uses real layout (not 500px intrinsic placeholder)
      submenu.dataset.active = '';
      // Force style/layout application before the first height read
      void submenu.offsetHeight;

      this.#applySubmenuHeight(submenu, isOverflow);
      this.#watchSubmenuSize(submenu, isOverflow);
      this.#scheduleSubmenuRemeasure(submenu, isOverflow);
    } else {
      this.#cleanupSubmenuWatchers();
      this.#applySubmenuHeight(null, isOverflow);
    }

    this.style.setProperty('--submenu-opacity', '1');
    // Safety-box / Safari hit-test tracking is mouse-hover only; on touch it
    // re-dispatches pointerenter after open and was toggling the menu shut.
    if (!this.#touchOpen) {
      this.#startPointerTracking(item, previouslyActiveItem);
    } else if (previouslyActiveItem) {
      this.#stopPointerTracking(previouslyActiveItem);
      document.body.removeEventListener('pointermove', this.#onPointerMove);
    }
  };

  /**
   * Deactivate the active item after a short delay (allows pointer to cross into mega menu)
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;
    if (!mediaQueryDesktop.matches) return;

    // Feature injection / touch sticky open: leave/blur must not close the panel.
    if (
      this.#suppressLeaveClose ||
      this.#touchOpen ||
      this.#isCoarsePointerEvent(event) ||
      (event.type === 'pointerleave' && (mediaHoverNone.matches || mediaPointerCoarse.matches))
    ) {
      return;
    }

    const menu = findSubmenu(this.#state.activeItem);
    const related = event.relatedTarget instanceof Node ? event.relatedTarget : null;
    const isMovingWithinMenu = related && menu?.contains(document.activeElement);
    const isMovingToSubmenu = related && menu?.contains(related);
    const isMovingToOverflowMenu =
      related && related.parentElement?.matches('[slot="overflow"]');
    // Pointer still over the open list item (bridge pseudo-element / child submenu)
    const activeListItem = this.#state.activeItem?.closest('.menu-list__list-item');
    const isMovingWithinListItem = related && activeListItem?.contains(related);

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu || isMovingWithinListItem) {
      clearTimeout(this.#deactivateTimer);
      this.#deactivateTimer = undefined;
      return;
    }

    clearTimeout(this.#deactivateTimer);
    this.#deactivateTimer = setTimeout(() => {
      this.#deactivateTimer = undefined;
      this.#deactivate();
    }, DEACTIVATE_DELAY_MS);
  }

  /**
   * Close immediately, ignoring sticky :hover (needed for touch dismiss).
   * @param {HTMLElement | null} [item]
   */
  #forceDeactivate = (item = this.#state.activeItem) => {
    this.#touchOpen = false;
    this.#ignoreToggleUntil = 0;
    this.#deactivate(item, { force: true });
  };

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   * @param {{ force?: boolean }} [options]
   */
  #deactivate = (item = this.#state.activeItem, options = {}) => {
    if (!item || item != this.#state.activeItem) return;

    const force = Boolean(options.force);

    // Don't deactivate if the overflow menu or overflow list is still being hovered
    // (mouse only — sticky :hover after touch would block intentional close)
    if (!force && !this.#touchOpen) {
      if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) return;

      // Pointer may already be over the submenu after crossing the padding gap
      const openSubmenu = findSubmenu(item);
      if (openSubmenu?.matches(':hover')) return;
    }

    const submenu = findSubmenu(item);

    clearTimeout(this.#hoverDispatchTimer);
    this.#hoverDispatchTimer = undefined;
    clearTimeout(this.#deactivateTimer);
    this.#deactivateTimer = undefined;
    this.#cleanupSubmenuWatchers();

    this.#touchOpen = false;
    this.#ignoreToggleUntil = 0;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0, 0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    document.body.removeEventListener('pointermove', this.#onPointerMove);
    this.#stopPointerTracking(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  /**
   * Measure mega menu panel height.
   * Use the inner content box only — outer scrollHeight includes the inner's
   * translateY(header) overflow and inflated the underlay (extra whitespace).
   * @param {HTMLElement} submenu
   * @returns {number}
   */
  #measureSubmenuHeight(submenu) {
    const inner = /** @type {HTMLElement | null} */ (submenu.querySelector('.menu-list__submenu-inner'));
    if (inner) {
      return Math.ceil(inner.offsetHeight || inner.getBoundingClientRect().height || 0);
    }
    return Math.ceil(submenu.offsetHeight || 0);
  }

  /**
   * Write --submenu-height and --full-open-header-height together (underlay + clip-path).
   * @param {HTMLElement | null | undefined} submenu
   * @param {boolean} [isOverflow]
   * @returns {number}
   */
  #applySubmenuHeight(submenu, isOverflow = false) {
    if (!this.headerComponent) return 0;

    let finalHeight = 0;

    if (submenu) {
      finalHeight = this.#measureSubmenuHeight(submenu);
    }

    if (isOverflow) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (submenu) {
        /* Overflow panel height can exceed the slotted submenu alone */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight, finalHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    // Skip no-op updates (ResizeObserver can re-fire when we write CSS vars)
    const nextSubmenu = `${finalHeight}px`;
    if (this.headerComponent.style.getPropertyValue('--submenu-height') === nextSubmenu) {
      return finalHeight;
    }

    const headerVisibleHeight = this.#getHeaderVisibleHeight();
    this.headerComponent.style.setProperty('--submenu-height', nextSubmenu);
    this.#setFullOpenHeaderHeight(finalHeight, headerVisibleHeight);
    return finalHeight;
  }

  /**
   * Remeasure after paint — first open often reads short height before layout settles.
   * @param {HTMLElement} submenu
   * @param {boolean} isOverflow
   */
  #scheduleSubmenuRemeasure(submenu, isOverflow) {
    if (this.#remeasureRaf) cancelAnimationFrame(this.#remeasureRaf);

    this.#remeasureRaf = requestAnimationFrame(() => {
      this.#remeasureRaf = requestAnimationFrame(() => {
        this.#remeasureRaf = undefined;
        if (!this.#state.activeItem) return;
        this.#applySubmenuHeight(submenu, isOverflow);
      });
    });
  }

  /**
   * Keep underlay/clip in sync when content size changes (images, hydration, fonts).
   * @param {HTMLElement} submenu
   * @param {boolean} isOverflow
   */
  #watchSubmenuSize(submenu, isOverflow) {
    this.#cleanupSubmenuWatchers();

    const inner = submenu.querySelector('.menu-list__submenu-inner');
    // Observe the content panel only — outer wrapper size is driven by CSS vars we set
    this.#submenuResizeObserver = new ResizeObserver(() => {
      if (!this.#state.activeItem) return;
      if (!submenu.dataset.active) submenu.dataset.active = '';
      this.#applySubmenuHeight(submenu, isOverflow);
    });
    if (inner instanceof HTMLElement) {
      this.#submenuResizeObserver.observe(inner);
    } else {
      this.#submenuResizeObserver.observe(submenu);
    }

    this.#onSubmenuImageLoad = () => {
      if (!this.#state.activeItem) return;
      this.#applySubmenuHeight(submenu, isOverflow);
    };
    submenu.querySelectorAll('img').forEach((img) => {
      if (!img.complete) {
        img.addEventListener('load', this.#onSubmenuImageLoad, { once: true });
      }
    });

    // Section hydration can inject featured content after first paint
    this.#submenuMutationObserver = new MutationObserver(() => {
      this.#scheduleSubmenuRemeasure(submenu, isOverflow);
    });
    this.#submenuMutationObserver.observe(submenu, { childList: true, subtree: true });
    setTimeout(() => {
      this.#submenuMutationObserver?.disconnect();
      this.#submenuMutationObserver = null;
    }, 1000);
  }

  #cleanupSubmenuWatchers() {
    if (this.#remeasureRaf) {
      cancelAnimationFrame(this.#remeasureRaf);
      this.#remeasureRaf = undefined;
    }
    this.#submenuResizeObserver?.disconnect();
    this.#submenuResizeObserver = null;
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
    this.#onSubmenuImageLoad = null;
  }

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    };

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Read the visible header height before submenu height writes invalidate layout.
   * @returns {number}
   */
  #getHeaderVisibleHeight() {
    if (!this.headerComponent) return 0;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    return isOverlapSituation && this.headerComponent.offsetHeight > 0
      ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
      : this.headerComponent.offsetHeight;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   * @param {number} headerVisibleHeight
   */
  #setFullOpenHeaderHeight(submenuHeight, headerVisibleHeight) {
    if (!this.headerComponent) return;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + headerVisibleHeight;

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
